import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Menu, Space, message } from "antd";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faTrash,
  faPenToSquare,
  faBan,
  faKey,
  faIdCard,
  faArrowCircleUp,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  updateOpenModalDeleteServer,
  updateOpenModalUpdateServer,
  updateServer,
} from "../../../redux/servers/serverSlice";
import { updateOpenModalTotpToken } from "../../../redux/authentication/authenticationSlice";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// INTERFACES
import { RootState } from "../../../app/store";
import { ServerInterface } from "../../../interfaces/Server.interface";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";

function ServersTableActions({ selectedItem }: { selectedItem: ServerInterface }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { userLogged, globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenModalTotpToken = () => {
    dispatch(updateServer(selectedItem));
    dispatch(updateOpenModalTotpToken(true));
  };

  const handleOpenModalEdit = () => {
    const idServer = formatUuidT(selectedItem.id) || "";
    if (idServer) {
      navigate(`/edge-management/server/edit?idServer=${idServer}`);
    }
  };

  const handleOpenModalDelete = () => {
    dispatch(updateServer(selectedItem));
    dispatch(updateOpenModalDeleteServer(true));
  };

  const handleOpenModalUpdate = () => {
    dispatch(updateServer(selectedItem));
    dispatch(updateOpenModalUpdateServer(true));
  };

  const handleGetId = async () => {
    if (!selectedItem.id) return;
    const id = formatUuidT(selectedItem.id);
    await navigator.clipboard.writeText(id);
    message.success(t("general.copyToClipboard"));
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "0",
    };

    const SECRET_CLIENT = hasPermission(Permission.ServersOauthCreate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faKey}
              text={t("edgeManagement.servers.secretClient")}
              onClick={handleOpenModalTotpToken}
            />
          ),
          key: "2",
        }
      : null;

    const GET_ID = {
      label: <TableActionsMenuItem icon={faIdCard} text="Get ID" onClick={handleGetId} />,
      key: "3",
    };

    const EDIT = hasPermission(Permission.ServersUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faPenToSquare}
              text={t("organizationManagement.edit")}
              onClick={handleOpenModalEdit}
            />
          ),
          key: "4",
        }
      : null;

    const UPDATE =
      isMasterAdmin(userLogged, globalOrganizationId) && selectedItem.id
        ? {
            label: (
              <TableActionsMenuItem
                icon={faArrowCircleUp}
                text={t("edgeServerUpdate.action")}
                onClick={handleOpenModalUpdate}
              />
            ),
            key: "4",
          }
        : null;

    const DELETE = hasPermission(Permission.ServersDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("organizationManagement.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "5",
        }
      : null;

    // RETURN OBJECT WITH VALID ACTIONS
    if (GET_ID || EDIT || DELETE || SECRET_CLIENT || UPDATE) {
      return [GET_ID, EDIT, SECRET_CLIENT, UPDATE, DELETE];
    }
    return [BLANK];
  };

  const menu = <Menu items={manageActions()} />;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown dropdownRender={() => menu} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default ServersTableActions;
